import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useMiniKit } from "@coinbase/onchainkit/minikit";
import { supabase } from '../supabaseClient';
import PostItNote from './PostItNote';
import MessageForm from './MessageForm';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import WelcomeModal from './WelcomeModal';
import ZoomControls from './ZoomControls';
import Footer from './Footer';

const BoardContainer = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background-image: url('https://www.transparenttextures.com/patterns/cork-board.png');
  background-color: #D2B48C;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

// Changed from absolute positioning to be part of the board
const CenterContent = styled.div`
  position: relative;
  width: 300px;
  height: 200px;
  margin: 0 auto;
  text-align: center;
  padding: 20px;
  z-index: 10;
  pointer-events: auto;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  transform: translateY(50px);
`;

const Title = styled.h1`
  font-size: 3rem;
  color: #333;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(255, 255, 255, 0.7);
  font-family: 'Permanent Marker', cursive, sans-serif;
`;

const AddButton = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s;
  
  &:hover {
    background-color: #357abd;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: auto;
`;

const ModalContent = styled.div`
  position: relative;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  pointer-events: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #555;
  
  &:hover {
    color: #000;
  }
`;

// Define the size of the virtual board - much larger than the viewport
const BOARD_WIDTH = 10000;
const BOARD_HEIGHT = 10000;
// Center point of the board
const CENTER_X = BOARD_WIDTH / 2;
const CENTER_Y = BOARD_HEIGHT / 2;
// Size of each note including margins
const NOTE_WIDTH = 260;
const NOTE_HEIGHT = 260;
// Title dimensions
const TITLE_WIDTH = 300;
const TITLE_HEIGHT = 200;

const CorkBoard = () => {
  // MiniKit hook for frame initialization - Step 3 implementation
  const { setFrameReady, isFrameReady } = useMiniKit();
  
  // The setFrameReady() function is called when your mini-app is ready to be shown
  useEffect(() => {
    if (!isFrameReady) {
      setFrameReady();
    }
  }, [setFrameReady, isFrameReady]);

  const [messages, setMessages] = useState([]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPosition, setStartDragPosition] = useState({ x: 0, y: 0 });
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const boardRef = useRef(null);
  const [notePositions, setNotePositions] = useState({});

  // Initialize the view to center of the board
  useEffect(() => {
    if (boardRef.current) {
      const viewportWidth = boardRef.current.offsetWidth;
      const viewportHeight = boardRef.current.offsetHeight;
      
      // Center the view on the middle of the board
      setPosition({
        x: (viewportWidth / 2) - CENTER_X,
        y: (viewportHeight / 2) - CENTER_Y
      });
    }
  }, []);

  // Check if it's the first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('healingOutLoud_hasVisited');
    if (!hasVisited) {
      setShowWelcome(true);
      localStorage.setItem('healingOutLoud_hasVisited', 'true');
    }
  }, []);

  // Fetch messages from Supabase
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        console.log('Fetching messages from HealingOutLoud table...');
        const { data, error } = await supabase
          .from('HealingOutLoud')
          .select('*');
        
        if (error) {
          console.error('Supabase error details:', error);
          console.error('Error message:', error.message);
          console.error('Error code:', error.code);
          throw error;
        }
        
        if (data) {
          console.log('Messages fetched successfully:', data);
          setMessages(data);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        setError('Failed to load messages. Please refresh the page to try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
    
    // Subscribe to new messages
    const subscription = supabase
      .channel('HealingOutLoud')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'HealingOutLoud' }, (payload) => {
        setMessages(prev => [...prev, payload.new]);
      })
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Calculate positions for all notes
  useEffect(() => {
    if (messages.length === 0) return;
    
    const newPositions = {};
    
    // Define cushioning around the title (extra space between title and notes)
    const TITLE_CUSHION = 50; // Increased from 30 to 50 pixels for more space
    
    // Simple layout algorithm that places notes in a spiral pattern starting from the title
    messages.forEach((msg, index) => {
      if (!msg.id) return;
      
      // Calculate position based on index
      let x, y;
      
      if (index === 0) {
        // First note: directly on top of the title with cushioning
        x = CENTER_X;
        y = CENTER_Y - TITLE_HEIGHT/2 - NOTE_HEIGHT/2 - TITLE_CUSHION;
      } else if (index === 1) {
        // Second note: directly below the title with cushioning
        x = CENTER_X;
        y = CENTER_Y + TITLE_HEIGHT/2 + NOTE_HEIGHT/2 + TITLE_CUSHION;
      } else if (index === 2) {
        // Third note: directly to the left of the title with cushioning
        x = CENTER_X - TITLE_WIDTH/2 - NOTE_WIDTH/2 - TITLE_CUSHION;
        y = CENTER_Y;
      } else if (index === 3) {
        // Fourth note: directly to the right of the title with cushioning
        x = CENTER_X + TITLE_WIDTH/2 + NOTE_WIDTH/2 + TITLE_CUSHION;
        y = CENTER_Y;
      } else if (index < 8) {
        // Next four notes: diagonal positions with cushioning
        const diagonalAngle = 45 + (index - 4) * 90; // 45, 135, 225, 315 degrees
        const radians = (diagonalAngle * Math.PI) / 180;
        // Increase diagonal distance for more spacing
        const diagonalDistance = Math.sqrt(Math.pow(TITLE_WIDTH/2 + NOTE_WIDTH/2 + TITLE_CUSHION + 20, 2) + 
                                          Math.pow(TITLE_HEIGHT/2 + NOTE_HEIGHT/2 + TITLE_CUSHION + 20, 2));
        
        x = CENTER_X + Math.cos(radians) * diagonalDistance;
        y = CENTER_Y + Math.sin(radians) * diagonalDistance;
      } else {
        // For remaining notes, place them in a spiral pattern with increased spacing
        // Calculate angle and radius based on index
        const angle = (index * 30) % 360; // 30 degrees per note
        const radius = Math.floor((index - 8) / 12) + 2.5; // Increased from 2 to 2.5 for more spacing
        
        // Convert to radians
        const radians = (angle * Math.PI) / 180;
        
        // Calculate position with increased spacing
        x = CENTER_X + Math.cos(radians) * radius * NOTE_WIDTH;
        y = CENTER_Y + Math.sin(radians) * radius * NOTE_HEIGHT;
      }
      
      // Add slight random offset for natural look (but keep it small)
      const offsetX = (Math.random() - 0.5) * 20;
      const offsetY = (Math.random() - 0.5) * 20;
      
      newPositions[msg.id] = { 
        x: x + offsetX, 
        y: y + offsetY,
        // Add slight rotation for natural look
        rotation: (Math.random() - 0.5) * 10
      };
    });
    
    setNotePositions(newPositions);
  }, [messages]);

  // Handle mouse down event for dragging
  const handleMouseDown = (e) => {
    // Don't start dragging if the form is open
    if (showForm) return;
    
    if (e.button !== 0) return; // Only left mouse button
    setIsDragging(true);
    setStartDragPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  // Handle mouse move event for dragging
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startDragPosition.x,
      y: e.clientY - startDragPosition.y
    });
    e.preventDefault();
  };

  // Handle mouse up event for dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setStartDragPosition({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - startDragPosition.x,
      y: touch.clientY - startDragPosition.y
    });
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Handle adding a new message
  const handleAddMessage = (newMessage) => {
    // The position will be calculated in the useEffect when the message is added to the messages array
    setShowForm(false);
  };

  // Handle zoom in
  const handleZoomIn = () => {
    const newZoomLevel = Math.min(zoomLevel + 0.1, 2);
    zoomAround(CENTER_X, CENTER_Y, newZoomLevel);
  };

  // Handle zoom out
  const handleZoomOut = () => {
    const newZoomLevel = Math.max(zoomLevel - 0.1, 0.5);
    zoomAround(CENTER_X, CENTER_Y, newZoomLevel);
  };

  // Reset view
  const handleResetView = () => {
    if (boardRef.current) {
      const viewportWidth = boardRef.current.offsetWidth;
      const viewportHeight = boardRef.current.offsetHeight;
      
      // Center the view on the middle of the board
      setPosition({
        x: (viewportWidth / 2) - CENTER_X,
        y: (viewportHeight / 2) - CENTER_Y
      });
    }
    setZoomLevel(1);
  };

  // Zoom around a specific point (in board coordinates)
  const zoomAround = (x, y, newZoomLevel) => {
    if (!boardRef.current) return;
    
    const viewportWidth = boardRef.current.offsetWidth;
    const viewportHeight = boardRef.current.offsetHeight;
    
    // Calculate the new position to keep the target point centered
    const newX = (viewportWidth / 2) - (x * newZoomLevel);
    const newY = (viewportHeight / 2) - (y * newZoomLevel);
    
    setPosition({ x: newX, y: newY });
    setZoomLevel(newZoomLevel);
  };

  // Handle wheel event for zooming
  const handleWheel = (e) => {
    // Prevent the default scroll behavior
    e.preventDefault();
    
    // Determine zoom direction
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    const newZoomLevel = Math.max(0.5, Math.min(2, zoomLevel + delta));
    
    // Don't do anything if we're already at the min/max zoom level
    if ((delta > 0 && zoomLevel >= 2) || (delta < 0 && zoomLevel <= 0.5)) {
      return;
    }
    
    // Zoom around the center of the board
    zoomAround(CENTER_X, CENTER_Y, newZoomLevel);
  };
  
  // Add wheel event listener
  useEffect(() => {
    const board = boardRef.current;
    if (board) {
      board.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        board.removeEventListener('wheel', handleWheel);
      };
    }
  }, [zoomLevel, position]);

  return (
    <BoardContainer
      ref={boardRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {isLoading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
          width: BOARD_WIDTH,
          height: BOARD_HEIGHT,
          position: 'absolute',
          transformOrigin: '0 0',
        }}
      >
        {/* Title and button are now part of the board */}
        <div style={{ position: 'absolute', left: CENTER_X - 150, top: CENTER_Y - 100 }}>
          <CenterContent>
            <Title>Healing Out Loud</Title>
            <AddButton onClick={() => setShowForm(true)}>
              Share Your Story
            </AddButton>
          </CenterContent>
        </div>
        
        {messages.map((msg, index) => {
          // Use stored position if available, or default to a position near the title
          const pos = msg.id && notePositions[msg.id] 
            ? notePositions[msg.id] 
            : { 
                x: CENTER_X + (Math.random() - 0.5) * TITLE_WIDTH, 
                y: CENTER_Y + (Math.random() - 0.5) * TITLE_HEIGHT,
                rotation: (Math.random() - 0.5) * 10
              };
          
          return (
            <PostItNote
              key={msg.id || index}
              message={msg.message}
              author={msg.author}
              x={pos.x}
              y={pos.y}
              rotation={pos.rotation}
              index={index + 1} // Start z-index from 1
            />
          );
        })}
      </div>

      <ZoomControls 
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleResetView}
      />

      <Footer />

      {showForm && (
        <ModalOverlay onClick={() => setShowForm(false)}>
          <ModalContent 
            onClick={(e) => {
              e.stopPropagation();
              // Prevent the click from closing the modal
              e.preventDefault();
            }}
          >
            <CloseButton onClick={() => setShowForm(false)}>×</CloseButton>
            <MessageForm onMessageSubmitted={handleAddMessage} />
          </ModalContent>
        </ModalOverlay>
      )}
      
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}
    </BoardContainer>
  );
};

export default CorkBoard; 